import { useMemo } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { BookingManagerApiService } from 'services/BookingManagerApi';
import { IPurchaseOrdersResponse } from 'services/BookingManagerApi/types/PurchaseOrdersResponse';

export interface IPurchaseOrderDetailData {
  reviewStatus: string | null;
  invoiceAmountCents: number | null;
}

export const usePurchaseOrdersQueries = (
  bookingManagerApi: BookingManagerApiService<AxiosWrapper<AxiosInstance>>,
  bookingUuid: string | undefined
) => {
  const queryClient = useQueryClient();

  return {
    getPurchaseOrdersVersionList: useQuery({
      queryKey: ['getPurchaseOrdersVersionList', bookingUuid],
      queryFn: async () => {
        if (!bookingUuid) {
          return null;
        }
        return bookingManagerApi.getPurchaseOrdersVersionList(bookingUuid);
      },
      enabled: !!bookingUuid,
    }),

    getPurchaseOrders: (version: { version: number; timestamp: string } | null) =>
      useQuery({
        queryKey: ['getPurchaseOrders', bookingUuid, version?.timestamp],
        queryFn: async () => {
          if (!bookingUuid || !version) {
            return null;
          }
          return bookingManagerApi.getPurchaseOrders(bookingUuid, version);
        },
        enabled: !!bookingUuid && !!version,
      }),

    invalidatePurchaseOrdersQueries: () => {
      queryClient.invalidateQueries({ queryKey: ['getPurchaseOrdersVersionList', bookingUuid] });
      queryClient.invalidateQueries({ queryKey: ['getPurchaseOrders', bookingUuid] });
    },
  };
};

export const usePurchaseOrderDetailMap = (
  bookingManagerApi: BookingManagerApiService<AxiosWrapper<AxiosInstance>>,
  purchaseOrdersData: IPurchaseOrdersResponse | undefined,
  enabled: boolean
): { data: Record<string, IPurchaseOrderDetailData>; isLoading: boolean } => {
  const poUuids = useMemo(() => {
    if (!purchaseOrdersData || !enabled) return [];
    return purchaseOrdersData.supplierPurchaseOrderGroups.flatMap(
      group => group.purchaseOrders.map(po => po.uuid)
    );
  }, [purchaseOrdersData, enabled]);

  const detailQueries = useQueries({
    queries: poUuids.map(uuid => ({
      queryKey: ['getPurchaseOrderDetail', uuid],
      queryFn: () => bookingManagerApi.getPurchaseOrderByUuid(uuid),
      enabled,
    })),
  });

  const isLoading = enabled && poUuids.length > 0 && detailQueries.some(q => q.isLoading);

  const data = useMemo(() => {
    const map: Record<string, IPurchaseOrderDetailData> = {};
    poUuids.forEach((uuid, index) => {
      const query = detailQueries[index];
      if (query?.data?.data) {
        map[uuid] = {
          reviewStatus: query.data.data.reviewStatus ?? null,
          invoiceAmountCents: query.data.data.invoiceAmountCents ?? null,
        };
      }
    });
    return map;
  }, [poUuids, detailQueries]);

  return { data, isLoading };
};
